import React, { useEffect, useState } from "react";
import axios from "axios";
import moment from "moment";
import {
  Box, Button, Card, CardContent, Chip, CircularProgress,
  Dialog, DialogActions, DialogContent, DialogTitle, FormControl, InputLabel, Select, MenuItem,
  Grid, IconButton, TextField, Typography, Snackbar, Alert,
  Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Accordion, AccordionSummary, AccordionDetails
} from "@mui/material";
import { Refresh, ExpandMore, Update, Visibility } from "@mui/icons-material";
import { api_urls } from "../../utils/api-urls/index.js";

const API = api_urls + "api/grow";

const statusColors = {
  pending: "warning", processing: "info", completed: "success", rejected: "error", refunded: "default"
};

const GrowPurchases = () => {
  const [purchases, setPurchases] = useState([]);
  const [categories, setCategories] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(false);
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterCategory, setFilterCategory] = useState("all");
  const [statusDialog, setStatusDialog] = useState({ open: false, purchase: null });
  const [newStatus, setNewStatus] = useState("");
  const [statusNotes, setStatusNotes] = useState("");
  const [detailDialog, setDetailDialog] = useState({ open: false, purchase: null });
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filterStatus !== "all") params.append("status", filterStatus);
      if (filterCategory !== "all") params.append("category", filterCategory);

      const [purRes, catRes, statsRes] = await Promise.all([
        axios.get(`${API}/purchases?${params.toString()}`),
        axios.get(`${API}/categories`),
        axios.get(`${API}/stats`),
      ]);
      if (purRes.data.success) setPurchases(purRes.data.data);
      if (catRes.data.success) setCategories(catRes.data.data);
      if (statsRes.data.success) setStats(statsRes.data.data);
    } catch (err) {
      setSnackbar({ open: true, message: "Failed to fetch data", severity: "error" });
    }
    setLoading(false);
  };

  useEffect(() => { fetchData(); }, [filterStatus, filterCategory]);

  const openStatusDialog = (purchase) => {
    setStatusDialog({ open: true, purchase });
    setNewStatus(purchase.status);
    setStatusNotes("");
  };

  const handleUpdateStatus = async () => {
    try {
      await axios.put(`${API}/purchases/${statusDialog.purchase._id}/status`, {
        status: newStatus, notes: statusNotes
      });
      setSnackbar({ open: true, message: "Status updated & notification sent!", severity: "success" });
      setStatusDialog({ open: false, purchase: null });
      fetchData();
    } catch (err) {
      setSnackbar({ open: true, message: "Error updating status", severity: "error" });
    }
  };

  const openDetail = (purchase) => {
    setDetailDialog({ open: true, purchase });
  };

  if (loading) return <Box display="flex" justifyContent="center" p={5}><CircularProgress /></Box>;

  return (
    <Box p={3}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">🌱 Grow - Purchase History</Typography>
        <Button variant="outlined" startIcon={<Refresh />} onClick={fetchData}>Refresh</Button>
      </Box>

      {/* Stats Cards */}
      <Grid container spacing={2} mb={3}>
        {[
          { label: "Total Orders", value: stats.total || 0, color: "#1976d2" },
          { label: "Pending", value: stats.pending || 0, color: "#ed6c02" },
          { label: "Processing", value: stats.processing || 0, color: "#0288d1" },
          { label: "Completed", value: stats.completed || 0, color: "#2e7d32" },
          { label: "Rejected", value: stats.rejected || 0, color: "#d32f2f" },
          { label: "Total Revenue", value: `₹${stats.totalRevenue || 0}`, color: "#9c27b0" },
        ].map((s, i) => (
          <Grid item xs={6} md={2} key={i}>
            <Card sx={{ bgcolor: s.color, color: "#fff" }}>
              <CardContent sx={{ py: 1.5, "&:last-child": { pb: 1.5 } }}>
                <Typography variant="caption">{s.label}</Typography>
                <Typography variant="h5" fontWeight="bold">{s.value}</Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Filters */}
      <Box display="flex" gap={2} mb={3}>
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>Status</InputLabel>
          <Select value={filterStatus} label="Status" onChange={(e) => setFilterStatus(e.target.value)}>
            <MenuItem value="all">All</MenuItem>
            <MenuItem value="pending">Pending</MenuItem>
            <MenuItem value="processing">Processing</MenuItem>
            <MenuItem value="completed">Completed</MenuItem>
            <MenuItem value="rejected">Rejected</MenuItem>
            <MenuItem value="refunded">Refunded</MenuItem>
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>Category</InputLabel>
          <Select value={filterCategory} label="Category" onChange={(e) => setFilterCategory(e.target.value)}>
            <MenuItem value="all">All</MenuItem>
            {categories.map((c) => <MenuItem key={c._id} value={c._id}>{c.name}</MenuItem>)}
          </Select>
        </FormControl>
      </Box>

      {/* Purchases Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow sx={{ bgcolor: "#f5f5f5" }}>
              <TableCell>Date</TableCell>
              <TableCell>User</TableCell>
              <TableCell>Service</TableCell>
              <TableCell>Category</TableCell>
              <TableCell>Amount</TableCell>
              <TableCell>Payment</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {purchases.length === 0 ? (
              <TableRow><TableCell colSpan={8} align="center">No purchases found</TableCell></TableRow>
            ) : purchases.map((p) => (
              <TableRow key={p._id} sx={{ bgcolor: p.status === 'pending' ? '#fff8e1' : 'inherit' }}>
                <TableCell>{moment(p.createdAt).format("DD MMM YYYY HH:mm")}</TableCell>
                <TableCell>
                  <strong>{p.user?.name || p.user?.firstName || p.userName || "N/A"}</strong>
                  <br /><small>{p.user?.phone || p.userMobile || ""}</small>
                </TableCell>
                <TableCell>
                  <strong>{p.service?.name || p.serviceName || "N/A"}</strong>
                </TableCell>
                <TableCell>
                  <Chip label={p.category?.name || p.categoryName || "N/A"} size="small" variant="outlined" />
                </TableCell>
                <TableCell><strong>₹{p.amount}</strong></TableCell>
                <TableCell>
                  <Chip label={p.paymentMethod === 'wallet' ? '💰 Wallet' : '💳 Razorpay'} size="small" variant="outlined" />
                </TableCell>
                <TableCell>
                  <Chip label={p.status.toUpperCase()} color={statusColors[p.status] || "default"} size="small" />
                </TableCell>
                <TableCell>
                  <IconButton onClick={() => openDetail(p)} color="primary" title="View Details"><Visibility /></IconButton>
                  <IconButton onClick={() => openStatusDialog(p)} color="warning" title="Update Status"><Update /></IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Update Status Dialog */}
      <Dialog open={statusDialog.open} onClose={() => setStatusDialog({ open: false, purchase: null })} maxWidth="sm" fullWidth>
        <DialogTitle>Update Status - {statusDialog.purchase?.serviceName || statusDialog.purchase?.service?.name}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
            User: {statusDialog.purchase?.userName || statusDialog.purchase?.user?.name} | 
            Amount: ₹{statusDialog.purchase?.amount}
          </Typography>
          <FormControl fullWidth sx={{ mb: 2, mt: 1 }}>
            <InputLabel>New Status</InputLabel>
            <Select value={newStatus} label="New Status" onChange={(e) => setNewStatus(e.target.value)}>
              <MenuItem value="pending">Pending</MenuItem>
              <MenuItem value="processing">Processing</MenuItem>
              <MenuItem value="completed">Completed</MenuItem>
              <MenuItem value="rejected">Rejected</MenuItem>
              <MenuItem value="refunded">Refunded</MenuItem>
            </Select>
          </FormControl>
          <TextField fullWidth label="Notes (will be sent to user)" value={statusNotes} onChange={(e) => setStatusNotes(e.target.value)} multiline rows={3} placeholder="Add notes about this status update..." />
          {newStatus === 'refunded' && statusDialog.purchase?.paymentMethod === 'wallet' && (
            <Alert severity="info" sx={{ mt: 2 }}>₹{statusDialog.purchase?.amount} will be refunded to user's wallet</Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setStatusDialog({ open: false, purchase: null })}>Cancel</Button>
          <Button variant="contained" onClick={handleUpdateStatus} color="warning">Update & Notify</Button>
        </DialogActions>
      </Dialog>

      {/* Detail Dialog */}
      <Dialog open={detailDialog.open} onClose={() => setDetailDialog({ open: false, purchase: null })} maxWidth="md" fullWidth>
        <DialogTitle>Purchase Details</DialogTitle>
        <DialogContent>
          {detailDialog.purchase && (
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12} md={6}>
                <Paper sx={{ p: 2 }}>
                  <Typography variant="h6" gutterBottom>👤 User Details</Typography>
                  <Typography><strong>Name:</strong> {detailDialog.purchase.user?.name || detailDialog.purchase.user?.firstName || detailDialog.purchase.userName}</Typography>
                  <Typography><strong>Phone:</strong> {detailDialog.purchase.user?.phone || detailDialog.purchase.userMobile}</Typography>
                  <Typography><strong>Email:</strong> {detailDialog.purchase.user?.email || "N/A"}</Typography>
                </Paper>
              </Grid>
              <Grid item xs={12} md={6}>
                <Paper sx={{ p: 2 }}>
                  <Typography variant="h6" gutterBottom>🛒 Order Details</Typography>
                  <Typography><strong>Service:</strong> {detailDialog.purchase.service?.name || detailDialog.purchase.serviceName}</Typography>
                  <Typography><strong>Category:</strong> {detailDialog.purchase.category?.name || detailDialog.purchase.categoryName}</Typography>
                  <Typography><strong>Amount:</strong> ₹{detailDialog.purchase.amount}</Typography>
                  <Typography><strong>Payment:</strong> {detailDialog.purchase.paymentMethod}</Typography>
                  <Typography><strong>Status:</strong> <Chip label={detailDialog.purchase.status} color={statusColors[detailDialog.purchase.status]} size="small" /></Typography>
                  <Typography><strong>Date:</strong> {moment(detailDialog.purchase.createdAt).format("DD MMM YYYY HH:mm:ss")}</Typography>
                  {detailDialog.purchase.razorpayPaymentId && <Typography><strong>Razorpay ID:</strong> {detailDialog.purchase.razorpayPaymentId}</Typography>}
                </Paper>
              </Grid>
              <Grid item xs={12}>
                <Paper sx={{ p: 2 }}>
                  <Typography variant="h6" gutterBottom>📋 Status History</Typography>
                  {(detailDialog.purchase.statusHistory || []).length === 0 ? (
                    <Typography color="textSecondary">No history</Typography>
                  ) : (
                    detailDialog.purchase.statusHistory.map((h, i) => (
                      <Box key={i} sx={{ display: "flex", alignItems: "center", mb: 1, p: 1, bgcolor: "#f9f9f9", borderRadius: 1 }}>
                        <Chip label={h.status} color={statusColors[h.status] || "default"} size="small" sx={{ mr: 1 }} />
                        <Typography variant="body2" sx={{ flex: 1 }}>{h.notes || "No notes"}</Typography>
                        <Typography variant="caption" color="textSecondary">{moment(h.updatedAt).format("DD MMM YYYY HH:mm")}</Typography>
                      </Box>
                    ))
                  )}
                </Paper>
              </Grid>
              {detailDialog.purchase.adminNotes && (
                <Grid item xs={12}>
                  <Paper sx={{ p: 2, bgcolor: "#fff3e0" }}>
                    <Typography variant="h6" gutterBottom>📝 Admin Notes</Typography>
                    <Typography>{detailDialog.purchase.adminNotes}</Typography>
                  </Paper>
                </Grid>
              )}
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailDialog({ open: false, purchase: null })}>Close</Button>
          <Button variant="contained" color="warning" onClick={() => { openStatusDialog(detailDialog.purchase); setDetailDialog({ open: false, purchase: null }); }}>Update Status</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snackbar.open} autoHideDuration={3000} onClose={() => setSnackbar({ ...snackbar, open: false })} anchorOrigin={{ vertical: "bottom", horizontal: "right" }}>
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
};

export default GrowPurchases;
